/**
 * Configurações da empresa — painel do Admin (F1.8 "Painel Admin mínimo", 04 §F1.8).
 *
 * O que esta tela é, em linguagem de operação: o lugar onde o Admin diz **onde** a empresa
 * trabalha (filiais), **como o trabalho se divide** (setores), **quando** se trabalha
 * (expediente de cada filial) e quais são os **parâmetros** que a operação ajusta sem chamar
 * programador. Mostra também, só para ler, as novidades ligadas para a empresa.
 *
 * Regras do contrato que este arquivo carrega:
 *
 *  1. **Uma ação principal por tela/seção** (docs/00 §7; 03 §3.5). Em cada seção existe um
 *     único botão em destaque. Quando um formulário está aberto, o destaque passa a ser o
 *     "Salvar" dele — nunca dois botões de mesmo peso disputando a atenção.
 *  2. **Botão que a pessoa não pode usar não some**: fica desabilitado e a frase que explica
 *     o porquê (`porQueNaoPode`) aparece na tela, ligada ao botão por `aria-describedby`.
 *     Botão que some faz a pessoa achar que o sistema quebrou e ligar para o gestor.
 *  3. **Escrever aqui é ação de Admin com segundo fator** — 03 §4.9: o grupo Organização
 *     (`filial`, `setor`) e a linha `empresa_config`/`filial_calendario`/`feature_flag*`
 *     dizem "só RPC (`administrar_usuarios` + AAL2)". A tela exige exatamente o mesmo que o
 *     servidor vai exigir, para não prometer o que o banco vai recusar.
 *  4. **Nenhuma substituição silenciosa de dados**: antes de gravar uma linha que já existe,
 *     a tela confere se outra pessoa mexeu nela; se mexeu, mostra o estado `conflito` em vez
 *     de passar por cima. O que foi digitado continua na tela.
 *  5. **Novidades (feature flags) são só leitura aqui**: ligar e desligar pertence à fase que
 *     introduz cada novidade. O painel mínimo mostra o que está valendo e explica o que cada
 *     uma liga — assim ninguém liga por engano um pedaço do sistema que ainda não foi testado.
 *  6. **Sem jargão**: nenhuma mensagem mostra código de erro, nome de tabela ou nome de função.
 *
 * Nomes reais de banco usados aqui (conferidos em supabase/migrations/2026081902 e 2026081909):
 * `public.filial(id, nome, codigo, ativa, atualizado_em)`,
 * `public.setor(id, chave, nome, ativo, atualizado_em)`,
 * `public.filial_calendario(filial_id, dias_semana, feriados, fuso, atualizado_em)`,
 * `public.empresa_config(...)`, `public.feature_flag(chave, descricao, padrao_ligada)` e a
 * RPC `public.resolver_feature_flags()`.
 */
import { useCallback, useEffect, useId, useMemo, useState, type CSSProperties } from 'react';
import { ESTADOS, textoDoEstado, type EstadoTela } from '../estados/tipos.js';
import { porQueNaoPode, type UsuarioSessao } from '../auth/sessao.js';
import { supabase } from '../lib/supabase.js';

// =====================================================================================
// 1. Estados de tela (03 §3.5 tabela A/B)
// =====================================================================================

/**
 * Os estados que ESTA tela trata. Declarar a lista (em vez de "tratar o que der") é o que
 * permite ao teste cobrar cada um deles.
 */
export const ESTADOS_DA_TELA = [
  'carregando',
  'vazio',
  'sem_internet',
  'sem_permissao',
  'versao_incompativel',
  'acao_em_andamento',
  'falha_recuperavel',
  'conflito',
  'sucesso_completo',
] as const satisfies readonly EstadoTela[];

/**
 * Os dois que NÃO se aplicam, com a justificativa exigida pela tabela B ("—" só com
 * justificativa na própria célula).
 */
export const ESTADOS_QUE_NAO_SE_APLICAM: Readonly<
  Record<'tiny_indisponivel' | 'sucesso_parcial', string>
> = {
  tiny_indisponivel:
    'Nada nesta tela vem do Tiny: filial, setor, expediente e parâmetros são cadastro nosso. ' +
    'Mostrar um aviso de Tiny aqui ensinaria a equipe a ignorar o aviso onde ele importa.',
  sucesso_parcial:
    'Configuração da empresa não entra na fila do aparelho: ou foi gravada no servidor, ou não ' +
    'foi. Dizer "concluído neste aparelho" seria mentira — e o Admin sairia achando que a ' +
    'mudança já vale para a equipe.',
};

/** Capacidade que o servidor exige para ESCREVER organização e configuração (03 §4.9). */
const CAPACIDADE_ADMINISTRAR = 'administrar_usuarios';

/** Capacidade que a RLS de `feature_flag` exige para LER as novidades (migration 2026081909). */
const CAPACIDADE_NOVIDADES = 'administrar_integracoes';

// =====================================================================================
// 2. O que a tela mostra e o que ela grava
// =====================================================================================

export type SecaoConfig = 'filiais' | 'setores' | 'expediente' | 'parametros' | 'novidades';

export interface FilialConfig {
  id: string;
  nome: string;
  codigo: string;
  ativa: boolean;
  atualizado_em: string | null;
}

export interface SetorConfig {
  id: string;
  chave: string;
  nome: string;
  ativo: boolean;
  atualizado_em: string | null;
}

/** Uma janela de trabalho de um dia: das 08:00 às 12:00, por exemplo. */
export interface JanelaTrabalho {
  inicio: string;
  fim: string;
}

export interface CalendarioFilial {
  filial_id: string;
  /** Chave = dia ISO da semana ('1' = segunda … '7' = domingo). */
  dias: Record<string, JanelaTrabalho[]>;
  /** Datas no formato 'AAAA-MM-DD', como o banco guarda. */
  feriados: string[];
  fuso: string;
  atualizado_em: string | null;
}

export interface NovidadeFlag {
  chave: string;
  descricao: string | null;
  padrao_ligada: boolean;
  /** Como está agora para quem abriu a tela (resultado de `resolver_feature_flags`). */
  ligada_agora: boolean | null;
}

export interface DadosConfiguracoes {
  filiais: FilialConfig[];
  setores: SetorConfig[];
  calendarios: CalendarioFilial[];
  parametros: Record<string, unknown> | null;
  novidades: NovidadeFlag[];
}

export interface EntradaFilial {
  /** `null` = filial nova. */
  id: string | null;
  nome: string;
  codigo: string;
  ativa: boolean;
  /** Carimbo que a tela leu antes de editar — a rede de proteção contra sobrescrita. */
  vistoEm: string | null;
}

export interface EntradaSetor {
  id: string | null;
  chave: string;
  nome: string;
  ativo: boolean;
  vistoEm: string | null;
}

export interface EntradaCalendario {
  filial_id: string;
  dias: Record<string, JanelaTrabalho[]>;
  feriados: string[];
  fuso: string;
  vistoEm: string | null;
}

/**
 * A porta de dados da tela. Existe como interface por dois motivos: o teste injeta uma
 * implementação de mentira sem subir banco, e o dia em que a leitura sair de uma consulta
 * direta para a cópia local do aparelho, só esta peça muda.
 */
export interface FonteConfiguracoes {
  carregar(opcoes: { incluirNovidades: boolean }): Promise<DadosConfiguracoes>;
  salvarFilial(entrada: EntradaFilial): Promise<void>;
  salvarSetor(entrada: EntradaSetor): Promise<void>;
  salvarCalendario(entrada: EntradaCalendario): Promise<void>;
}

// =====================================================================================
// 3. Vocabulário: nomes legíveis para o que o banco guarda em chave técnica
// =====================================================================================

const DIAS_DA_SEMANA = [
  { chave: '1', nome: 'Segunda-feira' },
  { chave: '2', nome: 'Terça-feira' },
  { chave: '3', nome: 'Quarta-feira' },
  { chave: '4', nome: 'Quinta-feira' },
  { chave: '5', nome: 'Sexta-feira' },
  { chave: '6', nome: 'Sábado' },
  { chave: '7', nome: 'Domingo' },
] as const;

/** O mesmo padrão que o banco usa quando a filial ainda não tem expediente próprio. */
const EXPEDIENTE_PADRAO: Record<string, JanelaTrabalho[]> = {
  '1': [{ inicio: '08:00', fim: '18:00' }],
  '2': [{ inicio: '08:00', fim: '18:00' }],
  '3': [{ inicio: '08:00', fim: '18:00' }],
  '4': [{ inicio: '08:00', fim: '18:00' }],
  '5': [{ inicio: '08:00', fim: '18:00' }],
  '6': [{ inicio: '08:00', fim: '12:00' }],
  '7': [],
};

interface DescricaoParametro {
  nome: string;
  explicacao: string;
  unidade?: string;
}

/**
 * Os parâmetros da empresa em português. A ordem é a da tela, não a do banco: primeiro o que
 * a equipe sente todo dia (prazos e alertas), depois o que é do aparelho, depois o resto.
 */
const PARAMETROS: ReadonlyArray<{ chave: string } & DescricaoParametro> = [
  {
    chave: 'sem_responsavel_horas',
    nome: 'Tarefa sem responsável vira alerta depois de',
    unidade: 'horas',
    explicacao: 'Tempo que uma tarefa pode ficar sem dono antes de aparecer no painel do gestor.',
  },
  {
    chave: 'processo_parado_horas',
    nome: 'Processo parado aparece no painel depois de',
    unidade: 'horas',
    explicacao:
      'Um processo sem nenhum avanço por esse tempo entra na lista de "precisa de atenção".',
  },
  {
    chave: 'conflito_escala_horas_uteis',
    nome: 'Conflito espera alguém resolver por',
    unidade: 'horas úteis',
    explicacao:
      'Quando duas pessoas responderam o mesmo campo, este é o tempo que a decisão espera com quem tem permissão para resolver.',
  },
  {
    chave: 'conflito_escala_admin_horas_uteis',
    nome: 'Conflito sobe para o Admin depois de',
    unidade: 'horas úteis',
    explicacao: 'Passado esse tempo sem decisão, o conflito passa a cobrar o Admin.',
  },
  {
    chave: 'validar_provisorio_horas_uteis',
    nome: 'Cadastro provisório precisa ser confirmado em',
    unidade: 'horas úteis',
    explicacao:
      'Cliente ou equipamento criado às pressas (Tiny fora do ar, por exemplo) não pode dormir sem alguém conferir.',
  },
  {
    chave: 'rascunho_abandono_dias',
    nome: 'Rascunho parado é considerado abandonado depois de',
    unidade: 'dias',
    explicacao: 'Serve para limpar a lista de quem já esqueceu do assunto — nada é apagado.',
  },
  {
    chave: 'lease_edicao_min',
    nome: 'O aviso "outra pessoa está preenchendo" expira em',
    unidade: 'minutos',
    explicacao: 'É só um aviso; ele nunca impede alguém de trabalhar sem internet.',
  },
  {
    chave: 'retencao_local_dias',
    nome: 'O aparelho guarda o que já subiu por',
    unidade: 'dias',
    explicacao: 'Depois desse prazo o aparelho libera espaço do que o servidor já confirmou.',
  },
  {
    chave: 'foto_qualidade_padrao',
    nome: 'Tamanho máximo da foto',
    unidade: 'pontos no lado maior',
    explicacao: 'Quanto maior, melhor a evidência e mais demorado o envio no campo.',
  },
  {
    chave: 'retencao_audio_meses',
    nome: 'Áudio de voz fica guardado por',
    unidade: 'meses',
    explicacao: 'O texto transcrito continua no checklist mesmo depois de o áudio sair.',
  },
  {
    chave: 'politica_2fa_padrao',
    nome: 'Quem precisa do segundo fator',
    explicacao:
      'Admin e gestor entram com código do aplicativo autenticador; os demais é opcional.',
  },
  {
    chave: 'orcamento_tiny',
    nome: 'Quanto do limite do Tiny cada uso pode consumir',
    explicacao:
      'Divide as chamadas por minuto entre o que roda no fundo e o que a pessoa está esperando na tela.',
  },
  {
    chave: 'epoca_restauracao',
    nome: 'Marco da última restauração',
    explicacao:
      'Muda quando o banco é restaurado de uma cópia de segurança; é o que faz os aparelhos reenviarem o que faltar.',
  },
];

// =====================================================================================
// 4. Conversas com o servidor
// =====================================================================================

function estaOnline(): boolean {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

function codigoDoErro(erro: unknown): string {
  if (erro && typeof erro === 'object' && 'code' in erro) {
    const codigo = (erro as { code?: unknown }).code;
    if (typeof codigo === 'string') return codigo;
  }
  return '';
}

function mensagemBruta(erro: unknown): string {
  if (erro instanceof Error) return erro.message;
  if (erro && typeof erro === 'object' && 'message' in erro) {
    const msg = (erro as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return '';
}

/**
 * Transforma qualquer falha em uma frase que o Admin entende. O que NUNCA pode sair daqui é
 * código HTTP, nome de função ou texto em inglês (03 §3.5 regra ii).
 *
 * Exceção proposital: quando a falha vem de uma regra do nosso próprio banco (P0001), a
 * mensagem já foi escrita em português para gente — repetir "não deu certo" no lugar dela
 * seria esconder a explicação boa que existe.
 */
function traduzirFalha(erro: unknown): string {
  const codigo = codigoDoErro(erro);
  const bruta = mensagemBruta(erro);
  const brutaMinuscula = bruta.toLowerCase();

  if (
    !estaOnline() ||
    brutaMinuscula.includes('failed to fetch') ||
    brutaMinuscula.includes('networkerror') ||
    brutaMinuscula.includes('network request failed')
  ) {
    return 'Sem internet — nada foi alterado. O que você digitou continua nesta tela.';
  }
  if (codigo === 'PGRST202') {
    return 'Esta parte das configurações ainda não pode ser gravada — o servidor ainda não recebeu essa peça. Nada foi alterado; avise quem cuida do sistema.';
  }
  if (codigo === 'P0001' && bruta.trim() !== '') {
    return bruta.trim();
  }
  if (codigo === '42501' || codigo === 'PGRST301' || codigo === 'PGRST116') {
    return ESTADOS.sem_permissao.texto;
  }
  if (codigo === '23505') {
    return 'Já existe outro cadastro com esse código — escolha um diferente.';
  }
  if (codigo === '23514' || codigo === '22P02') {
    return 'Algum valor não é aceito aqui. Confira o que foi digitado e tente de novo.';
  }
  return 'Não deu certo agora — nada foi alterado. Tente de novo.';
}

/** Lê `dias_semana` do banco com desconfiança: jsonb pode vir com qualquer formato. */
function lerDias(bruto: unknown): Record<string, JanelaTrabalho[]> {
  const saida: Record<string, JanelaTrabalho[]> = {};
  for (const dia of DIAS_DA_SEMANA) saida[dia.chave] = [];
  if (!bruto || typeof bruto !== 'object') return saida;

  for (const [chave, valor] of Object.entries(bruto as Record<string, unknown>)) {
    if (!Object.prototype.hasOwnProperty.call(saida, chave) || !Array.isArray(valor)) continue;
    const janelas: JanelaTrabalho[] = [];
    for (const item of valor) {
      if (Array.isArray(item) && typeof item[0] === 'string' && typeof item[1] === 'string') {
        janelas.push({ inicio: item[0], fim: item[1] });
      }
    }
    saida[chave] = janelas;
  }
  return saida;
}

/** Volta ao formato do banco: `{"1":[["08:00","18:00"]]}`. */
function escreverDias(dias: Record<string, JanelaTrabalho[]>): Record<string, [string, string][]> {
  const saida: Record<string, [string, string][]> = {};
  for (const dia of DIAS_DA_SEMANA) {
    const janelas = dias[dia.chave] ?? [];
    if (janelas.length > 0) saida[dia.chave] = janelas.map((j) => [j.inicio, j.fim]);
  }
  return saida;
}

function fonteSupabase(): FonteConfiguracoes {
  return {
    async carregar({ incluirNovidades }) {
      const cliente = supabase();

      // Uma consulta por assunto e todas ao mesmo tempo: a tela abre na velocidade da mais
      // lenta, e não na soma delas.
      const [filiais, setores, calendarios, parametros] = await Promise.all([
        cliente.from('filial').select('id, nome, codigo, ativa, atualizado_em').order('nome'),
        cliente.from('setor').select('id, chave, nome, ativo, atualizado_em').order('nome'),
        cliente
          .from('filial_calendario')
          .select('filial_id, dias_semana, feriados, fuso, atualizado_em'),
        cliente.from('empresa_config').select('*').maybeSingle(),
      ]);

      for (const resposta of [filiais, setores, calendarios, parametros]) {
        if (resposta.error) throw resposta.error;
      }

      let novidades: NovidadeFlag[] = [];
      if (incluirNovidades) {
        const [tabela, resolvidas] = await Promise.all([
          cliente.from('feature_flag').select('chave, descricao, padrao_ligada').order('chave'),
          cliente.rpc('resolver_feature_flags'),
        ]);
        if (tabela.error) throw tabela.error;
        // A resolução pode falhar sem derrubar a tela: sem ela mostramos o padrão e dizemos
        // que não deu para conferir como está agora.
        const agora = new Map<string, boolean>();
        if (!resolvidas.error && Array.isArray(resolvidas.data)) {
          for (const linha of resolvidas.data as { chave?: string; ligada?: boolean }[]) {
            if (typeof linha.chave === 'string' && typeof linha.ligada === 'boolean') {
              agora.set(linha.chave, linha.ligada);
            }
          }
        }
        novidades = ((tabela.data ?? []) as NovidadeFlag[]).map((f) => ({
          chave: f.chave,
          descricao: f.descricao ?? null,
          padrao_ligada: f.padrao_ligada,
          ligada_agora: agora.has(f.chave) ? (agora.get(f.chave) ?? null) : null,
        }));
      }

      return {
        filiais: (filiais.data ?? []) as FilialConfig[],
        setores: (setores.data ?? []) as SetorConfig[],
        calendarios: ((calendarios.data ?? []) as Record<string, unknown>[]).map((linha) => ({
          filial_id: String(linha['filial_id'] ?? ''),
          dias: lerDias(linha['dias_semana']),
          feriados: Array.isArray(linha['feriados']) ? (linha['feriados'] as string[]) : [],
          fuso: typeof linha['fuso'] === 'string' ? linha['fuso'] : '',
          atualizado_em: typeof linha['atualizado_em'] === 'string' ? linha['atualizado_em'] : null,
        })),
        parametros: (parametros.data as Record<string, unknown> | null) ?? null,
        novidades,
      };
    },

    // As três gravações passam por RPC `security definer` porque o cliente NÃO escreve nestas
    // tabelas (migration 2026081902 §6: sem GRANT de escrita e sem política de INSERT/UPDATE).
    // `p_visto_em` é o carimbo que a tela leu: é ele que deixa o servidor recusar a gravação
    // quando outra pessoa mexeu na linha no meio do caminho.
    async salvarFilial(entrada) {
      const { error } = await supabase().rpc('salvar_filial', {
        p_filial_id: entrada.id,
        p_nome: entrada.nome,
        p_codigo: entrada.codigo,
        p_ativa: entrada.ativa,
        p_visto_em: entrada.vistoEm,
      });
      if (error) throw error;
    },

    async salvarSetor(entrada) {
      const { error } = await supabase().rpc('salvar_setor', {
        p_setor_id: entrada.id,
        p_chave: entrada.chave,
        p_nome: entrada.nome,
        p_ativo: entrada.ativo,
        p_visto_em: entrada.vistoEm,
      });
      if (error) throw error;
    },

    async salvarCalendario(entrada) {
      const { error } = await supabase().rpc('salvar_calendario_filial', {
        p_filial_id: entrada.filial_id,
        p_dias_semana: escreverDias(entrada.dias),
        p_feriados: entrada.feriados,
        p_fuso: entrada.fuso,
        p_visto_em: entrada.vistoEm,
      });
      if (error) throw error;
    },
  };
}

// =====================================================================================
// 5. Formatação para leitura humana
// =====================================================================================

/** 'AAAA-MM-DD' → 'DD/MM/AAAA' sem passar por `Date`: fuso não pode virar o dia para trás. */
function dataLegivel(iso: string): string {
  const partes = iso.slice(0, 10).split('-');
  if (partes.length !== 3) return iso;
  return `${partes[2]}/${partes[1]}/${partes[0]}`;
}

function dataHoraLegivel(iso: string | null): string {
  if (!iso) return 'ainda não alterado';
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) return 'ainda não alterado';
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(
    quando,
  );
}

/** Transforma uma chave técnica em algo legível quando não há nome cadastrado para ela. */
function chaveLegivel(chave: string): string {
  const texto = chave.replace(/_/g, ' ').trim();
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

function valorLegivel(chave: string, valor: unknown, parametros: Record<string, unknown>): string {
  if (chave === 'epoca_restauracao') {
    const desde = parametros['epoca_desde'];
    return typeof desde === 'string' ? dataHoraLegivel(desde) : 'nunca restaurado';
  }
  if (valor === null || valor === undefined) return 'não definido';
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  if (typeof valor === 'number' || typeof valor === 'string') return String(valor);
  if (typeof valor === 'object') {
    return Object.entries(valor as Record<string, unknown>)
      .map(([k, v]) => `${chaveLegivel(k)}: ${String(v)}`)
      .join(' · ');
  }
  return String(valor);
}

// =====================================================================================
// 6. Estilos — tudo sai de design/tokens.css; nenhum valor literal de cor ou tamanho
// =====================================================================================

const est = {
  pagina: {
    maxWidth: 'var(--largura-conteudo-max)',
    margin: '0 auto',
    padding: 'var(--esp-6)',
  },
  eyebrow: {
    margin: 0,
    color: 'var(--cor-texto-suave)',
    fontSize: 'var(--font-size-legenda)',
    fontWeight: 'var(--peso-forte)',
    letterSpacing: 'var(--letra-eyebrow)',
    textTransform: 'uppercase',
  },
  titulo: {
    margin: 'var(--esp-2) 0 0',
    fontSize: 'var(--font-size-2xl)',
    letterSpacing: 'var(--letra-titulo)',
    lineHeight: 'var(--altura-linha-compacta)',
  },
  subtitulo: {
    margin: 'var(--esp-2) 0 0',
    color: 'var(--cor-texto-suave)',
    maxWidth: 'var(--largura-tablet)',
  },
  navegacao: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--esp-2)',
    margin: 'var(--esp-5) 0',
    borderBottom: '1px solid var(--cor-linha)',
    paddingBottom: 'var(--esp-3)',
  },
  aba: (ativa: boolean): CSSProperties => ({
    minHeight: 'var(--alvo-toque-min)',
    padding: '0 var(--esp-4)',
    borderRadius: 'var(--raio-pilula)',
    border: '1px solid var(--cor-linha)',
    background: ativa ? 'var(--cor-texto)' : 'var(--cor-superficie)',
    color: ativa ? 'var(--cor-superficie)' : 'var(--cor-texto)',
    fontWeight: 'var(--peso-medio)',
    cursor: 'pointer',
  }),
  cartao: {
    background: 'var(--cor-superficie)',
    border: '1px solid var(--cor-linha)',
    borderRadius: 'var(--raio)',
    boxShadow: 'var(--sombra-sm)',
    padding: 'var(--esp-5)',
  },
  cabecalhoSecao: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--esp-4)',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 'var(--esp-4)',
  },
  tituloSecao: {
    margin: 0,
    fontSize: 'var(--font-size-lg)',
    letterSpacing: 'var(--letra-titulo)',
  },
  apoio: {
    margin: 'var(--esp-2) 0 0',
    color: 'var(--cor-texto-suave)',
    maxWidth: 'var(--largura-tablet)',
    lineHeight: 'var(--altura-linha-texto)',
  },
  botaoPrincipal: (desabilitado: boolean): CSSProperties => ({
    minHeight: 'var(--alvo-toque-min)',
    padding: '0 var(--esp-5)',
    borderRadius: 'var(--raio-md)',
    border: '1px solid transparent',
    background: desabilitado ? 'var(--cor-superficie-3)' : 'var(--cor-marca)',
    color: desabilitado ? 'var(--cor-texto-suave-2)' : 'var(--cor-superficie)',
    fontWeight: 'var(--peso-forte)',
    cursor: desabilitado ? 'not-allowed' : 'pointer',
  }),
  botaoSecundario: (desabilitado: boolean): CSSProperties => ({
    minHeight: 'var(--alvo-toque-min)',
    padding: '0 var(--esp-4)',
    borderRadius: 'var(--raio-sm)',
    border: '1px solid var(--cor-linha-forte)',
    background: 'var(--cor-superficie)',
    color: desabilitado ? 'var(--cor-texto-suave-2)' : 'var(--cor-texto)',
    fontWeight: 'var(--peso-medio)',
    cursor: desabilitado ? 'not-allowed' : 'pointer',
  }),
  tabelaCaixa: { overflowX: 'auto' },
  tabela: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
  th: {
    padding: 'var(--esp-3)',
    borderBottom: '1px solid var(--cor-linha-forte)',
    color: 'var(--cor-texto-suave)',
    fontWeight: 'var(--peso-medio)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: 'var(--esp-3)',
    borderBottom: '1px solid var(--cor-linha)',
    verticalAlign: 'top',
  },
  campo: { display: 'grid', gap: 'var(--esp-1)', marginBottom: 'var(--esp-4)' },
  rotulo: { fontWeight: 'var(--peso-medio)' },
  entrada: {
    minHeight: 'var(--alvo-toque-min)',
    padding: '0 var(--esp-3)',
    borderRadius: 'var(--raio-sm)',
    border: '1px solid var(--cor-linha-forte)',
    background: 'var(--cor-superficie)',
    color: 'var(--cor-texto)',
    maxWidth: 'var(--largura-celular)',
  },
  dica: { color: 'var(--cor-texto-suave)', fontSize: 'var(--font-size-sm)' },
  linhaBotoes: { display: 'flex', flexWrap: 'wrap', gap: 'var(--esp-3)' },
  linhaDia: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 'var(--esp-3)',
    padding: 'var(--esp-3) 0',
    borderBottom: '1px solid var(--cor-linha)',
  },
  nomeDia: { minWidth: 'var(--esp-10)', fontWeight: 'var(--peso-medio)', flex: '1 1 8rem' },
  hora: {
    minHeight: 'var(--alvo-toque-min)',
    padding: '0 var(--esp-2)',
    borderRadius: 'var(--raio-xs)',
    border: '1px solid var(--cor-linha-forte)',
    background: 'var(--cor-superficie)',
    color: 'var(--cor-texto)',
  },
  selo: (ligada: boolean): CSSProperties => ({
    display: 'inline-block',
    padding: 'var(--esp-1) var(--esp-3)',
    borderRadius: 'var(--raio-pilula)',
    background: ligada ? 'var(--cor-sucesso-suave)' : 'var(--cor-superficie-3)',
    color: ligada ? 'var(--cor-sucesso)' : 'var(--cor-texto-suave)',
    fontWeight: 'var(--peso-medio)',
    whiteSpace: 'nowrap',
  }),
  vazio: {
    padding: 'var(--esp-6)',
    borderRadius: 'var(--raio-sm)',
    background: 'var(--cor-superficie-2)',
    border: '1px dashed var(--cor-linha-forte)',
    textAlign: 'center',
    color: 'var(--cor-texto-suave)',
  },
} satisfies Record<string, CSSProperties | ((...args: never[]) => CSSProperties)>;

function coresDoTom(tom: 'neutro' | 'atencao' | 'erro' | 'sucesso'): CSSProperties {
  switch (tom) {
    case 'sucesso':
      return {
        background: 'var(--cor-sucesso-suave)',
        borderColor: 'var(--cor-sucesso)',
        color: 'var(--cor-sucesso)',
      };
    case 'atencao':
      return {
        background: 'var(--cor-alerta-suave)',
        borderColor: 'var(--cor-alerta)',
        color: 'var(--cor-alerta)',
      };
    case 'erro':
      return {
        background: 'var(--cor-perigo-suave)',
        borderColor: 'var(--cor-perigo)',
        color: 'var(--cor-perigo)',
      };
    default:
      return {
        background: 'var(--cor-info-suave)',
        borderColor: 'var(--cor-info)',
        color: 'var(--cor-info)',
      };
  }
}

// =====================================================================================
// 7. A tela
// =====================================================================================

export interface ConfiguracoesProps {
  sessao: UsuarioSessao | null;
  /** Injetada pelo teste; em produção a tela fala com o Supabase. */
  fonte?: FonteConfiguracoes;
  secaoInicial?: SecaoConfig;
  /**
   * O invólucro do aplicativo avisa quando esta versão ficou abaixo do mínimo aceito. A tela
   * não descobre isso sozinha de propósito: a checagem de versão vale para o app inteiro e
   * ter duas fontes da mesma verdade produziria duas mensagens diferentes para o mesmo fato.
   */
  versaoIncompativel?: boolean;
}

interface Aviso {
  estado: EstadoTela;
  texto: string;
}

const SECOES: ReadonlyArray<{ chave: SecaoConfig; titulo: string }> = [
  { chave: 'filiais', titulo: 'Filiais' },
  { chave: 'setores', titulo: 'Setores' },
  { chave: 'expediente', titulo: 'Expediente' },
  { chave: 'parametros', titulo: 'Parâmetros' },
  { chave: 'novidades', titulo: 'Novidades' },
];

function secaoDoEndereco(): SecaoConfig {
  if (typeof window === 'undefined') return 'filiais';
  const alvo = window.location.hash.replace(/^#\/?/, '').split('/').pop() ?? '';
  const achada = SECOES.find((s) => s.chave === alvo);
  return achada ? achada.chave : 'filiais';
}

export function Configuracoes({
  sessao,
  fonte,
  secaoInicial,
  versaoIncompativel = false,
}: ConfiguracoesProps) {
  const porta = useMemo(() => fonte ?? fonteSupabase(), [fonte]);
  const idBase = useId();
  const idBloqueio = `${idBase}-bloqueio`;

  const [secao, setSecao] = useState<SecaoConfig>(() => secaoInicial ?? secaoDoEndereco());
  const [dados, setDados] = useState<DadosConfiguracoes | null>(null);
  const [carregando, setCarregando] = useState<boolean>(() => sessao !== null);
  const [falhaCarga, setFalhaCarga] = useState<string | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [online, setOnline] = useState<boolean>(estaOnline);

  const [formFilial, setFormFilial] = useState<EntradaFilial | null>(null);
  const [formSetor, setFormSetor] = useState<EntradaSetor | null>(null);
  const [filialDoExpediente, setFilialDoExpediente] = useState<string>('');
  const [rascunhoExpediente, setRascunhoExpediente] = useState<CalendarioFilial | null>(null);

  const motivoSemAdministrar = porQueNaoPode(sessao, CAPACIDADE_ADMINISTRAR, true);
  const podeAdministrar = motivoSemAdministrar === null;
  const motivoSemNovidades = porQueNaoPode(sessao, CAPACIDADE_NOVIDADES);
  const podeVerNovidades = motivoSemNovidades === null;

  /**
   * Ordem proposital: permissão primeiro. Dizer "sem internet" para quem nunca poderia salvar
   * manda a pessoa procurar sinal no pátio por um problema que não é de sinal.
   */
  const bloqueio: Aviso | null = motivoSemAdministrar
    ? { estado: 'sem_permissao', texto: motivoSemAdministrar }
    : versaoIncompativel
      ? {
          estado: 'versao_incompativel',
          texto:
            'Atualize o aplicativo para alterar configurações. A leitura desta tela continua funcionando.',
        }
      : !online
        ? {
            estado: 'sem_internet',
            texto:
              'Sem internet — configuração da empresa só muda com rede. O que você digitar continua nesta tela.',
          }
        : null;

  /** Só busca — quem guarda o resultado é quem chama. Separado para a primeira carga poder
   *  acontecer dentro de um efeito sem gravar estado de forma síncrona (regra do React 19). */
  const buscar = useCallback(
    () => porta.carregar({ incluirNovidades: podeVerNovidades }),
    [porta, podeVerNovidades],
  );

  // Primeira carga. `vivo` evita gravar estado de uma busca que já não interessa (a pessoa
  // trocou de seção ou saiu da tela antes de a resposta chegar).
  useEffect(() => {
    if (!sessao) return;
    let vivo = true;
    void buscar().then(
      (novos) => {
        if (!vivo) return;
        setDados(novos);
        setFalhaCarga(null);
        setCarregando(false);
      },
      (erro: unknown) => {
        if (!vivo) return;
        setFalhaCarga(traduzirFalha(erro));
        setCarregando(false);
      },
    );
    return () => {
      vivo = false;
    };
  }, [buscar, sessao]);

  /** Recarga pedida por uma ação (botão "Tentar de novo" ou depois de gravar). */
  const recarregar = useCallback(
    async (mostrarCarregando = true) => {
      if (!sessao) return;
      if (mostrarCarregando) setCarregando(true);
      try {
        setDados(await buscar());
        setFalhaCarga(null);
      } catch (erro) {
        setFalhaCarga(traduzirFalha(erro));
      } finally {
        setCarregando(false);
      }
    },
    [buscar, sessao],
  );

  useEffect(() => {
    // Bailout pelo valor: o navegador avisa "online" mesmo quando nada mudou, e redesenhar a
    // tela por um aviso repetido só atrapalha quem está no meio de um formulário.
    const atualizar = () => {
      const agora = estaOnline();
      setOnline((atual) => (atual === agora ? atual : agora));
    };
    window.addEventListener('online', atualizar);
    window.addEventListener('offline', atualizar);
    return () => {
      window.removeEventListener('online', atualizar);
      window.removeEventListener('offline', atualizar);
    };
  }, []);

  useEffect(() => {
    // Só troca de seção quando o endereço aponta para OUTRA seção. O aviso do navegador chega
    // também quando fomos nós que escrevemos o endereço — reagir a ele redesenharia a tela à toa.
    const aoTrocar = () => {
      const doEndereco = secaoDoEndereco();
      setSecao((atual) => (atual === doEndereco ? atual : doEndereco));
    };
    window.addEventListener('hashchange', aoTrocar);
    return () => window.removeEventListener('hashchange', aoTrocar);
  }, []);

  // Endereço navegável sem instalar roteador: escrever no endereço é sincronizar um sistema
  // externo (o navegador) com o estado da tela — por isso mora num efeito, e não no clique.
  useEffect(() => {
    const alvo = `configuracoes/${secao}`;
    if (window.location.hash.replace(/^#\/?/, '') !== alvo) {
      window.location.hash = alvo;
    }
  }, [secao]);

  function irPara(alvo: SecaoConfig) {
    setSecao(alvo);
    setAviso(null);
    setFormFilial(null);
    setFormSetor(null);
  }

  /**
   * Rede de proteção contra sobrescrita silenciosa: relê o carimbo da linha antes de gravar.
   * A garantia definitiva é do servidor (a RPC recebe `p_visto_em` e recusa quando a linha
   * mudou) — isto aqui é o que faz a TELA contar a verdade em vez de mandar a gravação e
   * torcer.
   */
  async function outraPessoaMexeu(
    vistoEm: string | null,
    escolher: (atuais: DadosConfiguracoes) => string | null | undefined,
  ): Promise<boolean> {
    const atuais = await porta.carregar({ incluirNovidades: false });
    const agora = escolher(atuais);
    return agora !== undefined && agora !== vistoEm;
  }

  async function executar(
    acao: () => Promise<void>,
    textoDeSucesso: string,
    aoDarCerto?: () => void,
  ) {
    setSalvando(true);
    setAviso({ estado: 'acao_em_andamento', texto: ESTADOS.acao_em_andamento.texto });
    try {
      await acao();
      aoDarCerto?.();
      await recarregar(false);
      setAviso({ estado: 'sucesso_completo', texto: textoDeSucesso });
    } catch (erro) {
      setAviso({ estado: 'falha_recuperavel', texto: traduzirFalha(erro) });
    } finally {
      setSalvando(false);
    }
  }

  /**
   * O texto do estado `conflito` é o padrão de `estados/tipos.ts`, especializado para dizer
   * QUAL coisa mudou. A justificativa exigida pelo contrato para o texto específico: aqui não
   * existe "o gestor vai decidir" — quem está na tela é o gestor, e ele decide na hora, desde
   * que veja o que o servidor tem antes de gravar por cima.
   */
  function avisarConflito(oQue: string) {
    const definicao = textoDoEstado('conflito', {
      texto: `Outra pessoa alterou ${oQue} enquanto você editava. O que você digitou continua aqui e recarregamos o que está no servidor — confira antes de salvar de novo.`,
      acao: 'Comparar e salvar de novo',
    });
    setAviso({ estado: 'conflito', texto: definicao.texto });
  }

  // ---------------------------------------------------------------------------------
  // Ações
  // ---------------------------------------------------------------------------------

  async function salvarFilial(entrada: EntradaFilial) {
    const nome = entrada.nome.trim();
    const codigo = entrada.codigo.trim();
    if (nome === '') return avisarCorrija('Dê um nome à filial — é ele que a equipe vê.');
    if (codigo === '')
      return avisarCorrija('O código curto identifica a filial nas filas de trabalho.');

    if (entrada.id !== null) {
      const alvo = entrada.id;
      if (
        await outraPessoaMexeu(
          entrada.vistoEm,
          (d) => d.filiais.find((f) => f.id === alvo)?.atualizado_em ?? null,
        )
      ) {
        await recarregar(false);
        return avisarConflito('esta filial');
      }
    }
    await executar(
      () => porta.salvarFilial({ ...entrada, nome, codigo }),
      entrada.id === null ? 'Filial criada — já pode receber trabalho.' : 'Filial salva.',
      () => setFormFilial(null),
    );
  }

  async function alternarFilial(filial: FilialConfig) {
    if (
      await outraPessoaMexeu(
        filial.atualizado_em,
        (d) => d.filiais.find((f) => f.id === filial.id)?.atualizado_em ?? null,
      )
    ) {
      await recarregar(false);
      return avisarConflito('esta filial');
    }
    await executar(
      () =>
        porta.salvarFilial({
          id: filial.id,
          nome: filial.nome,
          codigo: filial.codigo,
          ativa: !filial.ativa,
          vistoEm: filial.atualizado_em,
        }),
      filial.ativa
        ? 'Filial desativada — ela não aparece mais para trabalho novo; o histórico continua.'
        : 'Filial reativada.',
    );
  }

  async function salvarSetor(entrada: EntradaSetor) {
    const nome = entrada.nome.trim();
    const chave = entrada.chave.trim().toLowerCase();
    if (nome === '') return avisarCorrija('Dê um nome ao setor — é ele que a equipe vê.');
    if (entrada.id === null && !/^[a-z][a-z0-9_]*$/.test(chave)) {
      return avisarCorrija(
        'A identificação do setor não pode ter acento, espaço nem maiúscula: escreva algo como manutencao.',
      );
    }

    if (entrada.id !== null) {
      const alvo = entrada.id;
      if (
        await outraPessoaMexeu(
          entrada.vistoEm,
          (d) => d.setores.find((s) => s.id === alvo)?.atualizado_em ?? null,
        )
      ) {
        await recarregar(false);
        return avisarConflito('este setor');
      }
    }
    await executar(
      () => porta.salvarSetor({ ...entrada, nome, chave }),
      entrada.id === null ? 'Setor criado.' : 'Setor salvo.',
      () => setFormSetor(null),
    );
  }

  async function alternarSetor(setor: SetorConfig) {
    if (
      await outraPessoaMexeu(
        setor.atualizado_em,
        (d) => d.setores.find((s) => s.id === setor.id)?.atualizado_em ?? null,
      )
    ) {
      await recarregar(false);
      return avisarConflito('este setor');
    }
    await executar(
      () =>
        porta.salvarSetor({
          id: setor.id,
          chave: setor.chave,
          nome: setor.nome,
          ativo: !setor.ativo,
          vistoEm: setor.atualizado_em,
        }),
      setor.ativo ? 'Setor desativado — o histórico continua.' : 'Setor reativado.',
    );
  }

  async function salvarExpediente(rascunho: CalendarioFilial) {
    const problema = validarExpediente(rascunho);
    if (problema) return avisarCorrija(problema);

    if (
      await outraPessoaMexeu(
        rascunho.atualizado_em,
        (d) => d.calendarios.find((c) => c.filial_id === rascunho.filial_id)?.atualizado_em ?? null,
      )
    ) {
      await recarregar(false);
      return avisarConflito('o expediente desta filial');
    }
    await executar(
      () =>
        porta.salvarCalendario({
          filial_id: rascunho.filial_id,
          dias: rascunho.dias,
          feriados: rascunho.feriados,
          fuso: rascunho.fuso.trim(),
          vistoEm: rascunho.atualizado_em,
        }),
      'Expediente salvo — os prazos em horas úteis já contam por ele.',
    );
  }

  /**
   * Erro de digitação não é "o sistema falhou". Usamos o mesmo estado (falha recuperável),
   * mas com o texto do problema real e a ação "Corrigir" — dizer "vamos tentar de novo" para
   * um campo em branco faria a pessoa clicar de novo esperando outro resultado.
   */
  function avisarCorrija(texto: string) {
    const definicao = textoDoEstado('falha_recuperavel', { texto, acao: 'Corrigir' });
    setAviso({ estado: 'falha_recuperavel', texto: definicao.texto });
  }

  // ---------------------------------------------------------------------------------
  // Blocos de tela
  // ---------------------------------------------------------------------------------

  function faixa(item: Aviso, id?: string) {
    const definicao = ESTADOS[item.estado];
    return (
      <p
        id={id}
        style={{
          ...est.cartao,
          ...coresDoTom(definicao.tom),
          borderStyle: 'solid',
          borderWidth: '1px',
          margin: '0 0 var(--esp-4)',
          padding: 'var(--esp-4)',
        }}
      >
        {item.texto}
      </p>
    );
  }

  function acaoPrincipal(rotulo: string, aoClicar: () => void) {
    const impedido = bloqueio !== null || salvando;
    return (
      <button
        type="button"
        style={est.botaoPrincipal(impedido)}
        disabled={impedido}
        aria-describedby={bloqueio ? idBloqueio : undefined}
        onClick={aoClicar}
      >
        {salvando ? 'Salvando…' : rotulo}
      </button>
    );
  }

  function acaoSecundaria(rotulo: string, aoClicar: () => void) {
    const impedido = bloqueio !== null || salvando;
    return (
      <button
        type="button"
        style={est.botaoSecundario(impedido)}
        disabled={impedido}
        aria-describedby={bloqueio ? idBloqueio : undefined}
        onClick={aoClicar}
      >
        {rotulo}
      </button>
    );
  }

  function blocoVazio(texto: string, proximoPasso: string) {
    return (
      <div style={est.vazio}>
        <p style={{ margin: 0, fontWeight: 'var(--peso-medio)', color: 'var(--cor-texto)' }}>
          {texto}
        </p>
        <p style={{ margin: 'var(--esp-2) 0 0' }}>{proximoPasso}</p>
      </div>
    );
  }

  function renderFiliais() {
    if (formFilial) {
      const editando = formFilial.id !== null;
      return (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void salvarFilial(formFilial);
          }}
        >
          <h2 style={est.tituloSecao}>{editando ? 'Alterar filial' : 'Nova filial'}</h2>
          <div style={est.campo}>
            <label htmlFor={`${idBase}-filial-nome`} style={est.rotulo}>
              Nome da filial
            </label>
            <input
              id={`${idBase}-filial-nome`}
              style={est.entrada}
              value={formFilial.nome}
              onChange={(e) => setFormFilial({ ...formFilial, nome: e.target.value })}
            />
            <span style={est.dica}>É o nome que a equipe vê na tarefa e no processo.</span>
          </div>
          <div style={est.campo}>
            <label htmlFor={`${idBase}-filial-codigo`} style={est.rotulo}>
              Código curto
            </label>
            <input
              id={`${idBase}-filial-codigo`}
              style={est.entrada}
              value={formFilial.codigo}
              disabled={editando}
              onChange={(e) => setFormFilial({ ...formFilial, codigo: e.target.value })}
            />
            <span style={est.dica}>
              {editando
                ? 'O código não muda depois de criado: as filas de trabalho da filial apontam para ele.'
                : 'Curto e estável, como MTZ ou FIL2. Ele entra na identificação das filas de trabalho.'}
            </span>
          </div>
          <div style={est.campo}>
            <label htmlFor={`${idBase}-filial-ativa`} style={est.rotulo}>
              <input
                id={`${idBase}-filial-ativa`}
                type="checkbox"
                checked={formFilial.ativa}
                onChange={(e) => setFormFilial({ ...formFilial, ativa: e.target.checked })}
              />{' '}
              Recebe trabalho novo
            </label>
            <span style={est.dica}>
              Desmarcado, o histórico continua; só não entra trabalho novo.
            </span>
          </div>
          <div style={est.linhaBotoes}>
            {acaoPrincipal('Salvar filial', () => void salvarFilial(formFilial))}
            <button
              type="button"
              style={est.botaoSecundario(false)}
              onClick={() => setFormFilial(null)}
            >
              Cancelar
            </button>
          </div>
        </form>
      );
    }

    const lista = dados?.filiais ?? [];
    return (
      <>
        <div style={est.cabecalhoSecao}>
          <div>
            <h2 style={est.tituloSecao}>Filiais</h2>
            <p style={est.apoio}>
              Onde o trabalho acontece. Todo processo nasce em uma filial — por isso ela nunca fica
              em branco.
            </p>
          </div>
          {acaoPrincipal('Nova filial', () =>
            setFormFilial({ id: null, nome: '', codigo: '', ativa: true, vistoEm: null }),
          )}
        </div>
        {lista.length === 0 ? (
          blocoVazio(
            'Nenhuma filial cadastrada ainda',
            'Crie a primeira para a equipe poder abrir processos.',
          )
        ) : (
          <div style={est.tabelaCaixa}>
            <table style={est.tabela}>
              <caption style={{ ...est.dica, textAlign: 'left', paddingBottom: 'var(--esp-2)' }}>
                Filiais da empresa
              </caption>
              <thead>
                <tr>
                  <th style={est.th} scope="col">
                    Filial
                  </th>
                  <th style={est.th} scope="col">
                    Código
                  </th>
                  <th style={est.th} scope="col">
                    Recebe trabalho novo
                  </th>
                  <th style={est.th} scope="col">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((filial) => (
                  <tr key={filial.id}>
                    <th style={est.td} scope="row">
                      {filial.nome}
                    </th>
                    <td style={est.td}>{filial.codigo}</td>
                    <td style={est.td}>
                      <span style={est.selo(filial.ativa)}>{filial.ativa ? 'Sim' : 'Não'}</span>
                    </td>
                    <td style={est.td}>
                      <div style={est.linhaBotoes}>
                        {acaoSecundaria(`Renomear ${filial.nome}`, () =>
                          setFormFilial({
                            id: filial.id,
                            nome: filial.nome,
                            codigo: filial.codigo,
                            ativa: filial.ativa,
                            vistoEm: filial.atualizado_em,
                          }),
                        )}
                        {acaoSecundaria(
                          filial.ativa ? `Desativar ${filial.nome}` : `Ativar ${filial.nome}`,
                          () => void alternarFilial(filial),
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  function renderSetores() {
    if (formSetor) {
      const editando = formSetor.id !== null;
      return (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void salvarSetor(formSetor);
          }}
        >
          <h2 style={est.tituloSecao}>{editando ? 'Alterar setor' : 'Novo setor'}</h2>
          <div style={est.campo}>
            <label htmlFor={`${idBase}-setor-nome`} style={est.rotulo}>
              Nome do setor
            </label>
            <input
              id={`${idBase}-setor-nome`}
              style={est.entrada}
              value={formSetor.nome}
              onChange={(e) => setFormSetor({ ...formSetor, nome: e.target.value })}
            />
            <span style={est.dica}>Vendas, Manutenção, Expedição, Estoque…</span>
          </div>
          <div style={est.campo}>
            <label htmlFor={`${idBase}-setor-chave`} style={est.rotulo}>
              Identificação do setor
            </label>
            <input
              id={`${idBase}-setor-chave`}
              style={est.entrada}
              value={formSetor.chave}
              disabled={editando}
              onChange={(e) => setFormSetor({ ...formSetor, chave: e.target.value })}
            />
            <span style={est.dica}>
              {editando
                ? 'A identificação não muda depois de criada: a fila de trabalho do setor aponta para ela.'
                : 'Letras minúsculas sem acento, como manutencao. É por ela que a fila de trabalho encontra o setor.'}
            </span>
          </div>
          <div style={est.campo}>
            <label htmlFor={`${idBase}-setor-ativo`} style={est.rotulo}>
              <input
                id={`${idBase}-setor-ativo`}
                type="checkbox"
                checked={formSetor.ativo}
                onChange={(e) => setFormSetor({ ...formSetor, ativo: e.target.checked })}
              />{' '}
              Em uso
            </label>
          </div>
          <div style={est.linhaBotoes}>
            {acaoPrincipal('Salvar setor', () => void salvarSetor(formSetor))}
            <button
              type="button"
              style={est.botaoSecundario(false)}
              onClick={() => setFormSetor(null)}
            >
              Cancelar
            </button>
          </div>
        </form>
      );
    }

    const lista = dados?.setores ?? [];
    return (
      <>
        <div style={est.cabecalhoSecao}>
          <div>
            <h2 style={est.tituloSecao}>Setores</h2>
            <p style={est.apoio}>
              Setor é também equipe e fila: quem está no setor de uma filial recebe o trabalho
              daquela fila.
            </p>
          </div>
          {acaoPrincipal('Novo setor', () =>
            setFormSetor({ id: null, chave: '', nome: '', ativo: true, vistoEm: null }),
          )}
        </div>
        {lista.length === 0 ? (
          blocoVazio(
            'Nenhum setor cadastrado ainda',
            'Crie os setores para o trabalho ter para onde ir.',
          )
        ) : (
          <div style={est.tabelaCaixa}>
            <table style={est.tabela}>
              <caption style={{ ...est.dica, textAlign: 'left', paddingBottom: 'var(--esp-2)' }}>
                Setores da empresa
              </caption>
              <thead>
                <tr>
                  <th style={est.th} scope="col">
                    Setor
                  </th>
                  <th style={est.th} scope="col">
                    Identificação
                  </th>
                  <th style={est.th} scope="col">
                    Em uso
                  </th>
                  <th style={est.th} scope="col">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((setor) => (
                  <tr key={setor.id}>
                    <th style={est.td} scope="row">
                      {setor.nome}
                    </th>
                    <td style={est.td}>{setor.chave}</td>
                    <td style={est.td}>
                      <span style={est.selo(setor.ativo)}>{setor.ativo ? 'Sim' : 'Não'}</span>
                    </td>
                    <td style={est.td}>
                      <div style={est.linhaBotoes}>
                        {acaoSecundaria(`Renomear ${setor.nome}`, () =>
                          setFormSetor({
                            id: setor.id,
                            chave: setor.chave,
                            nome: setor.nome,
                            ativo: setor.ativo,
                            vistoEm: setor.atualizado_em,
                          }),
                        )}
                        {acaoSecundaria(
                          setor.ativo ? `Desativar ${setor.nome}` : `Ativar ${setor.nome}`,
                          () => void alternarSetor(setor),
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  function abrirExpediente(filialId: string) {
    setFilialDoExpediente(filialId);
    setAviso(null);
    if (filialId === '') {
      setRascunhoExpediente(null);
      return;
    }
    const existente = dados?.calendarios.find((c) => c.filial_id === filialId);
    setRascunhoExpediente(
      existente
        ? { ...existente, dias: clonarDias(existente.dias), feriados: [...existente.feriados] }
        : {
            filial_id: filialId,
            dias: clonarDias(EXPEDIENTE_PADRAO),
            feriados: [],
            fuso: 'America/Sao_Paulo',
            atualizado_em: null,
          },
    );
  }

  function renderExpediente() {
    const filiais = dados?.filiais ?? [];
    const semExpedienteProprio =
      rascunhoExpediente !== null &&
      !(dados?.calendarios ?? []).some((c) => c.filial_id === rascunhoExpediente.filial_id);

    return (
      <>
        <div style={est.cabecalhoSecao}>
          <div>
            <h2 style={est.tituloSecao}>Expediente da filial</h2>
            <p style={est.apoio}>
              Dias, horários e feriados de cada filial. É isto que faz o{' '}
              <strong>prazo em horas úteis</strong> ser justo com quem está em campo: um prazo de 4
              horas aberto às 17h não vence às 21h — ele continua a contar na manhã seguinte, quando
              a equipe volta a trabalhar. Sem este calendário, o sistema cobraria atraso de quem não
              tinha como cumprir.
            </p>
          </div>
        </div>

        {filiais.length === 0 ? (
          blocoVazio(
            'Nenhuma filial cadastrada ainda',
            'Cadastre a filial antes de definir o expediente dela.',
          )
        ) : (
          <>
            <div style={est.campo}>
              <label htmlFor={`${idBase}-expediente-filial`} style={est.rotulo}>
                Filial
              </label>
              <select
                id={`${idBase}-expediente-filial`}
                style={est.entrada}
                value={filialDoExpediente}
                onChange={(e) => abrirExpediente(e.target.value)}
              >
                <option value="">Escolha uma filial</option>
                {filiais.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.nome}
                  </option>
                ))}
              </select>
            </div>

            {rascunhoExpediente === null ? (
              blocoVazio(
                'Nenhuma filial escolhida',
                'Escolha a filial acima para ver e ajustar o expediente.',
              )
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void salvarExpediente(rascunhoExpediente);
                }}
              >
                {semExpedienteProprio && (
                  <p style={est.apoio}>
                    Esta filial ainda não tem expediente próprio. Mostramos o padrão da empresa —
                    salve para valer para ela.
                  </p>
                )}

                <fieldset style={{ border: 0, padding: 0, margin: 'var(--esp-4) 0' }}>
                  <legend style={est.rotulo}>Dias e horários de trabalho</legend>
                  {DIAS_DA_SEMANA.map((dia) => {
                    const janelas = rascunhoExpediente.dias[dia.chave] ?? [];
                    return (
                      <div key={dia.chave} style={est.linhaDia}>
                        <span style={est.nomeDia}>{dia.nome}</span>
                        {janelas.length === 0 ? (
                          <span style={est.dica}>Não se trabalha neste dia</span>
                        ) : (
                          janelas.map((janela, indice) => (
                            <span
                              key={indice}
                              style={{ display: 'flex', alignItems: 'center', gap: 'var(--esp-2)' }}
                            >
                              <input
                                type="time"
                                style={est.hora}
                                value={janela.inicio}
                                aria-label={`Começo do ${indice + 1}º horário de ${dia.nome}`}
                                onChange={(e) =>
                                  setRascunhoExpediente(
                                    trocarJanela(rascunhoExpediente, dia.chave, indice, {
                                      ...janela,
                                      inicio: e.target.value,
                                    }),
                                  )
                                }
                              />
                              <span aria-hidden="true">até</span>
                              <input
                                type="time"
                                style={est.hora}
                                value={janela.fim}
                                aria-label={`Fim do ${indice + 1}º horário de ${dia.nome}`}
                                onChange={(e) =>
                                  setRascunhoExpediente(
                                    trocarJanela(rascunhoExpediente, dia.chave, indice, {
                                      ...janela,
                                      fim: e.target.value,
                                    }),
                                  )
                                }
                              />
                              {acaoSecundaria(
                                `Remover o ${indice + 1}º horário de ${dia.nome}`,
                                () =>
                                  setRascunhoExpediente(
                                    removerJanela(rascunhoExpediente, dia.chave, indice),
                                  ),
                              )}
                            </span>
                          ))
                        )}
                        {acaoSecundaria(`Adicionar horário em ${dia.nome}`, () =>
                          setRascunhoExpediente(
                            adicionarJanela(rascunhoExpediente, dia.chave, {
                              inicio: '08:00',
                              fim: '12:00',
                            }),
                          ),
                        )}
                      </div>
                    );
                  })}
                  <p style={est.dica}>
                    Dá para ter mais de um horário no mesmo dia — é assim que se fecha para o
                    almoço.
                  </p>
                </fieldset>

                <fieldset style={{ border: 0, padding: 0, margin: 'var(--esp-4) 0' }}>
                  <legend style={est.rotulo}>Feriados desta filial</legend>
                  {rascunhoExpediente.feriados.length === 0 ? (
                    <p style={est.dica}>
                      Nenhum feriado cadastrado. Em feriado, o prazo não corre.
                    </p>
                  ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {rascunhoExpediente.feriados.map((data, indice) => (
                        <li key={`${data}-${indice}`} style={est.linhaDia}>
                          <span style={est.nomeDia}>{dataLegivel(data)}</span>
                          {acaoSecundaria(`Remover o feriado de ${dataLegivel(data)}`, () =>
                            setRascunhoExpediente({
                              ...rascunhoExpediente,
                              feriados: rascunhoExpediente.feriados.filter((_, i) => i !== indice),
                            }),
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div style={est.campo}>
                    <label htmlFor={`${idBase}-feriado-novo`} style={est.rotulo}>
                      Adicionar feriado
                    </label>
                    <input
                      id={`${idBase}-feriado-novo`}
                      type="date"
                      style={est.entrada}
                      value=""
                      onChange={(e) => {
                        const nova = e.target.value;
                        if (nova === '' || rascunhoExpediente.feriados.includes(nova)) return;
                        setRascunhoExpediente({
                          ...rascunhoExpediente,
                          feriados: [...rascunhoExpediente.feriados, nova].sort(),
                        });
                      }}
                    />
                  </div>
                </fieldset>

                <div style={est.campo}>
                  <label htmlFor={`${idBase}-fuso`} style={est.rotulo}>
                    Fuso horário da filial
                  </label>
                  <input
                    id={`${idBase}-fuso`}
                    style={est.entrada}
                    value={rascunhoExpediente.fuso}
                    onChange={(e) =>
                      setRascunhoExpediente({ ...rascunhoExpediente, fuso: e.target.value })
                    }
                  />
                  <span style={est.dica}>
                    Ex.: America/Sao_Paulo. É por ele que "às 18h" quer dizer 18h para quem está lá.
                  </span>
                </div>

                <p style={est.dica}>
                  Última alteração: {dataHoraLegivel(rascunhoExpediente.atualizado_em)}.
                </p>

                <div style={est.linhaBotoes}>
                  {acaoPrincipal(
                    'Salvar expediente',
                    () => void salvarExpediente(rascunhoExpediente),
                  )}
                </div>
              </form>
            )}
          </>
        )}
      </>
    );
  }

  function renderParametros() {
    const parametros = dados?.parametros;
    return (
      <>
        <div style={est.cabecalhoSecao}>
          <div>
            <h2 style={est.tituloSecao}>Parâmetros da empresa</h2>
            <p style={est.apoio}>
              São os números que decidem quando o sistema cobra atenção. Ficam aqui, e não dentro do
              programa, para que mudar "48 horas parado" não dependa de uma nova versão do
              aplicativo. Nesta primeira versão do painel eles são só de leitura.
            </p>
          </div>
        </div>
        {!parametros ? (
          blocoVazio(
            'Nenhum parâmetro definido ainda',
            'Assim que a empresa for configurada, os valores aparecem aqui.',
          )
        ) : (
          <div style={est.tabelaCaixa}>
            <table style={est.tabela}>
              <caption style={{ ...est.dica, textAlign: 'left', paddingBottom: 'var(--esp-2)' }}>
                Parâmetros que a operação ajusta
              </caption>
              <thead>
                <tr>
                  <th style={est.th} scope="col">
                    Parâmetro
                  </th>
                  <th style={est.th} scope="col">
                    Valor
                  </th>
                  <th style={est.th} scope="col">
                    O que isso muda na prática
                  </th>
                </tr>
              </thead>
              <tbody>
                {PARAMETROS.filter((p) => p.chave in parametros).map((p) => (
                  <tr key={p.chave}>
                    <th style={est.td} scope="row">
                      {p.nome}
                    </th>
                    <td style={est.td}>
                      {valorLegivel(p.chave, parametros[p.chave], parametros)}
                      {p.unidade ? ` ${p.unidade}` : ''}
                    </td>
                    <td style={est.td}>{p.explicacao}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  function renderNovidades() {
    if (!podeVerNovidades) {
      return (
        <>
          <h2 style={est.tituloSecao}>Novidades</h2>
          {faixa({
            estado: 'sem_permissao',
            texto: motivoSemNovidades ?? ESTADOS.sem_permissao.texto,
          })}
        </>
      );
    }

    const lista = dados?.novidades ?? [];
    return (
      <>
        <div style={est.cabecalhoSecao}>
          <div>
            <h2 style={est.tituloSecao}>Novidades</h2>
            <p style={est.apoio}>
              Partes do sistema que podem ser liberadas aos poucos. Aqui você só enxerga o que está
              valendo: ligar e desligar é feito na fase que traz cada novidade, para que ninguém
              libere por engano algo que ainda não foi testado com a equipe.
            </p>
          </div>
        </div>
        {lista.length === 0 ? (
          blocoVazio(
            'Nenhuma novidade cadastrada',
            'Quando uma parte nova for preparada, ela aparece aqui.',
          )
        ) : (
          <div style={est.tabelaCaixa}>
            <table style={est.tabela}>
              <caption style={{ ...est.dica, textAlign: 'left', paddingBottom: 'var(--esp-2)' }}>
                Novidades da empresa (só leitura)
              </caption>
              <thead>
                <tr>
                  <th style={est.th} scope="col">
                    Novidade
                  </th>
                  <th style={est.th} scope="col">
                    O que ela liga
                  </th>
                  <th style={est.th} scope="col">
                    Como está para você
                  </th>
                  <th style={est.th} scope="col">
                    Padrão da empresa
                  </th>
                </tr>
              </thead>
              <tbody>
                {lista.map((flag) => (
                  <tr key={flag.chave}>
                    <th style={est.td} scope="row">
                      {chaveLegivel(flag.chave)}
                    </th>
                    <td style={est.td}>
                      {flag.descricao ??
                        'Sem explicação cadastrada — peça a quem cuida do sistema.'}
                    </td>
                    <td style={est.td}>
                      {flag.ligada_agora === null ? (
                        <span style={est.dica}>Não deu para conferir agora</span>
                      ) : (
                        <span style={est.selo(flag.ligada_agora)}>
                          {flag.ligada_agora ? 'Ligada' : 'Desligada'}
                        </span>
                      )}
                    </td>
                    <td style={est.td}>{flag.padrao_ligada ? 'Ligada' : 'Desligada'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }

  function renderSecao() {
    switch (secao) {
      case 'filiais':
        return renderFiliais();
      case 'setores':
        return renderSetores();
      case 'expediente':
        return renderExpediente();
      case 'parametros':
        return renderParametros();
      case 'novidades':
        return renderNovidades();
    }
  }

  // ---------------------------------------------------------------------------------
  // Montagem
  // ---------------------------------------------------------------------------------

  return (
    <main style={est.pagina} aria-labelledby={`${idBase}-titulo`}>
      <header>
        <p style={est.eyebrow}>Painel do Admin</p>
        <h1 id={`${idBase}-titulo`} style={est.titulo}>
          Configurações
        </h1>
        <p style={est.subtitulo}>
          Filiais, setores, expediente e os parâmetros que a operação ajusta sem chamar programador.
        </p>
      </header>

      <nav style={est.navegacao} aria-label="Seções das configurações">
        {SECOES.map((s) => (
          <button
            key={s.chave}
            type="button"
            style={est.aba(secao === s.chave)}
            aria-current={secao === s.chave ? 'page' : undefined}
            onClick={() => irPara(s.chave)}
          >
            {s.titulo}
          </button>
        ))}
      </nav>

      {/* A região viva existe sempre no documento: leitor de tela só anuncia mudança dentro de
          uma região que já estava lá quando a tela abriu. */}
      <div aria-live="polite" aria-atomic="true">
        {aviso && faixa(aviso)}
      </div>

      {sessao && bloqueio && faixa(bloqueio, idBloqueio)}

      <section style={est.cartao}>
        {!sessao ? (
          faixa({ estado: 'sem_permissao', texto: 'Entre novamente para continuar.' })
        ) : carregando ? (
          <p style={est.apoio}>{ESTADOS.carregando.texto}</p>
        ) : falhaCarga ? (
          <>
            {faixa({ estado: 'falha_recuperavel', texto: falhaCarga })}
            <button
              type="button"
              style={est.botaoPrincipal(false)}
              onClick={() => void recarregar()}
            >
              Tentar de novo
            </button>
          </>
        ) : (
          renderSecao()
        )}
      </section>

      {podeAdministrar && (
        <p style={{ ...est.dica, marginTop: 'var(--esp-4)' }}>
          Alterações aqui valem para a empresa inteira e ficam registradas com o seu nome.
        </p>
      )}
    </main>
  );
}

// =====================================================================================
// 8. Auxiliares puros do expediente (fora do componente: são regra, não tela)
// =====================================================================================

function clonarDias(dias: Record<string, JanelaTrabalho[]>): Record<string, JanelaTrabalho[]> {
  const saida: Record<string, JanelaTrabalho[]> = {};
  for (const dia of DIAS_DA_SEMANA)
    saida[dia.chave] = (dias[dia.chave] ?? []).map((j) => ({ ...j }));
  return saida;
}

function trocarJanela(
  calendario: CalendarioFilial,
  dia: string,
  indice: number,
  nova: JanelaTrabalho,
): CalendarioFilial {
  const dias = clonarDias(calendario.dias);
  const janelas = dias[dia] ?? [];
  janelas[indice] = nova;
  dias[dia] = janelas;
  return { ...calendario, dias };
}

function adicionarJanela(
  calendario: CalendarioFilial,
  dia: string,
  nova: JanelaTrabalho,
): CalendarioFilial {
  const dias = clonarDias(calendario.dias);
  dias[dia] = [...(dias[dia] ?? []), nova];
  return { ...calendario, dias };
}

function removerJanela(
  calendario: CalendarioFilial,
  dia: string,
  indice: number,
): CalendarioFilial {
  const dias = clonarDias(calendario.dias);
  dias[dia] = (dias[dia] ?? []).filter((_, i) => i !== indice);
  return { ...calendario, dias };
}

/**
 * Devolve a frase do problema, ou `null` quando está tudo certo.
 *
 * A sobreposição é checada porque duas janelas que se cruzam fariam a mesma hora contar duas
 * vezes no prazo — o funcionário perderia horas de prazo sem entender por quê.
 */
export function validarExpediente(calendario: CalendarioFilial): string | null {
  const hora = /^([01]\d|2[0-3]):[0-5]\d$/;

  for (const dia of DIAS_DA_SEMANA) {
    const janelas = [...(calendario.dias[dia.chave] ?? [])];
    for (const janela of janelas) {
      if (!hora.test(janela.inicio) || !hora.test(janela.fim)) {
        return `Confira os horários de ${dia.nome}: use o formato 08:00.`;
      }
      if (janela.fim <= janela.inicio) {
        return `Em ${dia.nome}, o fim do horário precisa ser depois do começo.`;
      }
    }
    janelas.sort((a, b) => a.inicio.localeCompare(b.inicio));
    for (let i = 1; i < janelas.length; i += 1) {
      const anterior = janelas[i - 1];
      const atual = janelas[i];
      if (anterior && atual && atual.inicio < anterior.fim) {
        return `Em ${dia.nome} há dois horários que se cruzam. Ajuste para que um comece depois que o outro termina.`;
      }
    }
  }

  const temTrabalho = DIAS_DA_SEMANA.some((d) => (calendario.dias[d.chave] ?? []).length > 0);
  if (!temTrabalho) {
    return 'Marque pelo menos um dia de trabalho: sem nenhum, um prazo em horas úteis nunca venceria.';
  }

  for (const feriado of calendario.feriados) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(feriado)) {
      return 'Confira as datas dos feriados.';
    }
  }

  if (calendario.fuso.trim() === '') {
    return 'Informe o fuso horário da filial, como America/Sao_Paulo.';
  }

  return null;
}
